import { BOOKING_TYPES } from "@/lib/booking-types";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { CalendarOff, Loader2, Trash2, UserPlus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  addBlockedSlot,
  createManualBooking,
  listBlockedSlots,
  removeBlockedSlot,
} from "@/lib/admin-schedule.functions";

export const Route = createFileRoute("/_authenticated/admin/schedule")({
  component: SchedulePage,
});

function formatRange(startIso: string, endIso: string) {
  const opts: Intl.DateTimeFormatOptions = {
    timeZone: "Africa/Johannesburg",
    weekday: "short",
    day: "numeric",
    month: "short",
  };
  const start = new Date(startIso);
  const end = new Date(endIso);
  const time = (d: Date) =>
    d.toLocaleTimeString("en-ZA", { timeZone: "Africa/Johannesburg", hour: "2-digit", minute: "2-digit", hour12: false });
  return `${start.toLocaleDateString("en-ZA", opts)} · ${time(start)} – ${time(end)}`;
}

function SchedulePage() {
  const qc = useQueryClient();
  const listBlocks = useServerFn(listBlockedSlots);
  const addBlock = useServerFn(addBlockedSlot);
  const delBlock = useServerFn(removeBlockedSlot);
  const addBooking = useServerFn(createManualBooking);

  const { data: blocks = [], isPending } = useQuery({
    queryKey: ["admin", "blocked-slots"],
    queryFn: () => listBlocks(),
  });

  // Block time form
  const [bDate, setBDate] = useState("");
  const [bStart, setBStart] = useState("09:00");
  const [bEnd, setBEnd] = useState("17:00");
  const [allDay, setAllDay] = useState(false);
  const [reason, setReason] = useState("");

  const blockMut = useMutation({
    mutationFn: () =>
      addBlock({ data: { date: bDate, start_time: bStart, end_time: bEnd, all_day: allDay, reason } }),
    onSuccess: () => {
      toast.success("Time blocked — no one can book it online.");
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin", "blocked-slots"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not block that time"),
  });

  const unblockMut = useMutation({
    mutationFn: (id: string) => delBlock({ data: { id } }),
    onSuccess: () => {
      toast.success("Block removed");
      qc.invalidateQueries({ queryKey: ["admin", "blocked-slots"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not remove that block"),
  });

  // Manual patient form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState(45);
  const [serviceId, setServiceId] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const bookingMut = useMutation({
    mutationFn: () =>
      addBooking({
        data: {
          client_name: name,
          email,
          date,
          time,
          duration_minutes: duration,
          service_id: serviceId || undefined,
          phone,
          notes,
          send_email: sendEmail,
        },
      }),
    onSuccess: () => {
      toast.success(sendEmail ? "Appointment added and confirmation email sent." : "Appointment added.");
      setName(""); setEmail(""); setPhone(""); setNotes("");
      qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
      qc.invalidateQueries({ queryKey: ["admin", "overview"] });
      qc.invalidateQueries({ queryKey: ["admin", "patients"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not add the appointment"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Schedule</h1>
        <p className="text-sm text-muted-foreground">Block off time you are not available, and add appointments yourself.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><UserPlus className="h-4 w-4" /> Add a patient appointment</CardTitle>
            <CardDescription>Name, email, date and time is all that is needed.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Patient name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@email.com" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Time</Label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Duration (minutes)</Label>
                <Input type="number" min={15} max={240} step={15} value={duration} onChange={(e) => setDuration(Number(e.target.value))} />
              </div>
              <div>
                <Label className="text-xs">Phone (optional)</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="072 000 0000" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Service</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={serviceId}
                onChange={(e) => {
                  setServiceId(e.target.value);
                  const t = BOOKING_TYPES.find((x) => x.id === e.target.value);
                  if (t) setDuration(t.duration);
                }}
              >
                <option value="">Not specified (price quoted at appointment)</option>
                {BOOKING_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} · {t.durationLabel} · {t.price} cash</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason for visit" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(v === true)} />
              Send the patient a confirmation email
            </label>
            <Button
              className="w-full"
              disabled={bookingMut.isPending || name.trim().length < 2 || !email.includes("@") || !date}
              onClick={() => bookingMut.mutate()}
            >
              {bookingMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Add appointment
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><CalendarOff className="h-4 w-4" /> Block off time</CardTitle>
            <CardDescription>Blocked time disappears from the online booking calendar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="time" value={bStart} disabled={allDay} onChange={(e) => setBStart(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Until</Label>
                <Input type="time" value={bEnd} disabled={allDay} onChange={(e) => setBEnd(e.target.value)} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={allDay} onCheckedChange={(v) => setAllDay(v === true)} />
              Whole day
            </label>
            <div>
              <Label className="text-xs">Reason (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Away, CPD course" />
            </div>
            <Button className="w-full" variant="secondary" disabled={!bDate || blockMut.isPending} onClick={() => blockMut.mutate()}>
              {blockMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Block this time
            </Button>

            <div className="pt-2">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Current blocks</p>
              {isPending ? (
                <div className="flex h-20 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>
              ) : blocks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No time blocked off.</p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {blocks.map((b) => (
                    <li key={b.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span>
                        {formatRange(b.start, b.end)}
                        {b.reason ? <span className="text-muted-foreground"> · {b.reason}</span> : null}
                      </span>
                      <Button size="icon" variant="ghost" disabled={unblockMut.isPending} onClick={() => unblockMut.mutate(b.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
